/**
 * Context-based hooks for user data access
 *
 * These hooks provide access to user/student data from StudentDataContext.
 * Grade selection is now derived from URL parameters - navigate to change grade.
 */

import { useStudentData } from "../StudentDataContext";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";

/**
 * Hook to access user state from context
 */
export function useUserSelector() {
  const ctx = useStudentData();

  return {
    headerDetails: ctx.headerDetails,
    selectedGrade: ctx.selectedGrade,
    studentsFamilyAvatars: ctx.studentsFamilyAvatars,
    dashboardDetails: ctx.dashboardDetails,
    dismissedNotificationIds: ctx.dismissedNotificationIds,
    mostRecentWork: ctx.mostRecentWork,
    changedGrade: ctx.changedGrade,
    activatingGradeCode: ctx.activatingGradeCode,

    getHeaderDetailsRequestStatus: ctx.headerDetailsStatus,
    getHeaderDetailsRequestError: null,
    getGradeChangeOtpRequestStatus: ctx.getGradeChangeOtpRequestStatus,
    getGradeChangeOtpRequestError: null,
    validateGradeChangeOtpRequestStatus: ctx.validateGradeChangeOtpRequestStatus,
    validateGradeChangeOtpRequestError: ctx.validateGradeChangeOtpRequestError,
    studentsFamilyUsedAvatarsRequestStatus: REQUEST_STATUS.IDLE,
    studentsFamilyUsedAvatarsRequestError: null,
    updateAvatarRequestStatus: ctx.updateAvatarRequestStatus,
    updateAvatarRequestError: null,
    completeOrientationRequestStatus: ctx.completeOrientationRequestStatus,
    completeOrientationRequestError: null,
    updateUserStateRequestStatus: REQUEST_STATUS.IDLE,
    updateUserStateRequestError: null,
    changePasswordRequestStatus: ctx.changePasswordRequestStatus,
    changePasswordRequestError: ctx.changePasswordRequestError,
    getDashboardDetailsRequestStatus: ctx.getDashboardDetailsRequestStatus,
    getDashboardDetailsRequestError: null
  };
}

/**
 * Hook to access user actions from context
 *
 * Note: Grade selection is now derived from URL parameters.
 * To change grade, navigate to a route with the desired gradeId.
 */
export function useUserDispatch() {
  const ctx = useStudentData();

  return {
    fetchHeaderDetails: ctx.fetchHeaderDetails,
    ensureFamilyAvatars: ctx.ensureFamilyAvatars,
    fetchFamilyAvatars: ctx.fetchFamilyAvatars,
    fetchGradeChangeOtp: ctx.fetchGradeChangeOtp,
    validateGradeChangeOtp: ctx.validateGradeChangeOtp,
    updateAvatar: ctx.updateAvatar,
    changePassword: ctx.changePassword,
    completeOrientation: ctx.completeOrientation,
    updateUserState: ctx.updateUserState,
    setChangedGrade: ctx.setChangedGrade,
    setActivatingGrade: ctx.setActivatingGradeCode,
    resetAvatarState: ctx.resetAvatarState,
    resetChangePasswordState: ctx.resetChangePasswordState,
    dismissNotification: ctx.dismissNotification
  };
}
